/**
 * Status-driven accent palettes.
 *
 * The "none" entry MUST mirror the brand defaults declared in
 * `src/index.css` :root. Drift between the two will cause a visible
 * theme flash the moment the runtime applies a status theme.
 */

import { AgentStatus } from '../types'

export interface StatusTheme {
  /** Primary accent color */
  accent: string
  /** Lighter variant for backgrounds / soft fills */
  accentLight: string
  /** Darker variant for emphasis */
  accentDark: string
  /** Soft overlay (~10% opacity) */
  accentSoft: string
  /** Glow / shadow tint */
  glow: string
  /** Button shadow */
  buttonShadow: string
  /** CSS class name applied to <html> for status-scoped styles */
  className: string
  /** Logo pulse animation speed in ms (0 = no pulse) */
  logoPulseMs: number
  /** Whether the logo dot is visible */
  logoDotVisible: boolean
}

export const STATUS_THEMES: Record<AgentStatus | 'none', StatusTheme> = {
  none: {
    accent: '#00D9A0',
    accentLight: 'rgba(0, 217, 160, 0.15)',
    accentDark: '#00B386',
    accentSoft: 'rgba(0, 217, 160, 0.10)',
    glow: 'rgba(0, 217, 160, 0.12)',
    buttonShadow: '0 8px 24px rgba(0, 217, 160, 0.28)',
    className: 'status-theme-none',
    logoPulseMs: 0,
    logoDotVisible: true,
  },

  newborn: {
    accent: '#8B5CF6',
    accentLight: '#EDE9FE',
    accentDark: '#6D28D9',
    accentSoft: 'rgba(139, 92, 246, 0.10)',
    glow: 'rgba(139, 92, 246, 0.15)',
    buttonShadow: '0 8px 24px rgba(139, 92, 246, 0.28)',
    className: 'status-theme-newborn',
    logoPulseMs: 3000,
    logoDotVisible: true,
  },

  alive: {
    accent: '#10B981',
    accentLight: '#D1FAE5',
    accentDark: '#047857',
    accentSoft: 'rgba(16, 185, 129, 0.10)',
    glow: 'rgba(16, 185, 129, 0.12)',
    buttonShadow: '0 8px 24px rgba(16, 185, 129, 0.28)',
    className: 'status-theme-alive',
    logoPulseMs: 0,
    logoDotVisible: true,
  },

  comfortable: {
    accent: '#22C55E',
    accentLight: '#DCFCE7',
    accentDark: '#15803D',
    accentSoft: 'rgba(34, 197, 94, 0.10)',
    glow: 'rgba(34, 197, 94, 0.12)',
    buttonShadow: '0 8px 24px rgba(34, 197, 94, 0.28)',
    className: 'status-theme-comfortable',
    logoPulseMs: 0,
    logoDotVisible: true,
  },

  low: {
    accent: '#F59E0B',
    accentLight: '#FEF3C7',
    accentDark: '#B45309',
    accentSoft: 'rgba(245, 158, 11, 0.10)',
    glow: 'rgba(245, 158, 11, 0.15)',
    buttonShadow: '0 8px 24px rgba(245, 158, 11, 0.28)',
    className: 'status-theme-low',
    logoPulseMs: 3000,
    logoDotVisible: true,
  },

  dying: {
    accent: '#F97316',
    accentLight: '#FFEDD5',
    accentDark: '#C2410C',
    accentSoft: 'rgba(249, 115, 22, 0.10)',
    glow: 'rgba(249, 115, 22, 0.20)',
    buttonShadow: '0 8px 24px rgba(249, 115, 22, 0.30)',
    className: 'status-theme-dying',
    logoPulseMs: 2000,
    logoDotVisible: true,
  },

  critical: {
    accent: '#EF4444',
    accentLight: '#FEE2E2',
    accentDark: '#B91C1C',
    accentSoft: 'rgba(239, 68, 68, 0.10)',
    glow: 'rgba(239, 68, 68, 0.25)',
    buttonShadow: '0 8px 24px rgba(239, 68, 68, 0.35)',
    className: 'status-theme-critical',
    logoPulseMs: 500,
    logoDotVisible: true,
  },

  dead: {
    accent: '#6B7280',
    accentLight: '#F3F4F6',
    accentDark: '#1F2937',
    accentSoft: 'rgba(107, 114, 128, 0.10)',
    glow: 'rgba(107, 114, 128, 0.08)',
    buttonShadow: '0 8px 24px rgba(107, 114, 128, 0.15)',
    className: 'status-theme-dead',
    logoPulseMs: 0,
    logoDotVisible: false,
  },

  provisioning: {
    accent: '#8B5CF6',
    accentLight: '#EDE9FE',
    accentDark: '#6D28D9',
    accentSoft: 'rgba(139, 92, 246, 0.10)',
    glow: 'rgba(139, 92, 246, 0.15)',
    buttonShadow: '0 8px 24px rgba(139, 92, 246, 0.28)',
    className: 'status-theme-provisioning',
    logoPulseMs: 2000,
    logoDotVisible: true,
  },

  provision_failed: {
    accent: '#6B7280',
    accentLight: '#F3F4F6',
    accentDark: '#1F2937',
    accentSoft: 'rgba(107, 114, 128, 0.10)',
    glow: 'rgba(107, 114, 128, 0.08)',
    buttonShadow: '0 8px 24px rgba(107, 114, 128, 0.15)',
    className: 'status-theme-provision-failed',
    logoPulseMs: 0,
    logoDotVisible: false,
  },
}

/** All status-theme class names — used by the runtime to clear stale classes. */
export const STATUS_THEME_CLASSES: readonly string[] = Object.values(
  STATUS_THEMES,
).map((t) => t.className)
